import React, {
  useCallback,
  useMemo,
  useState
} from "react";
import { useMappedState } from "redux-react-hook";
import * as _ from "lodash";
import { toast } from "react-toastify";

import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { LikeToDo } from "src/library/components/LikeToDo";
import { Table } from "src/core/dashboard/components/widgets";
import { ImportModal } from "src/core/shared/components/ImportModal";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  IProductApprovalList,
  ProductApprovalRequestName,
  useProductApprovalApi
} from "src/modules/product-approval/api/product-approval.api";
import { PageProps } from "src/pages/Router/routes";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import type { IStore } from "src/core/shared/store";
import { ProductApprovalExportModal } from "src/modules/product-approval/components/ProductApprovalExportModal";
import {
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { getApprovalListColumns } from "src/core/shared/components/tables/approvalListColumns";

const defaultPagination: PaginatedParams<IProductApprovalList> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "DESC" ] ]
};

const AprrovalListsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history } = props;
  const [ showImportModal, setShowImportModal ] = useState(false);
  const [ showExportModal, setShowExportModal ] = React.useState(false);
  const [ triggerFtpImportResponse, triggerFtpImportRequest ] = useImportSettingApi("TRIGGER_FTP_IMPORT");
  const [ approvalListsPaginatedResponse, getApprovalListPaginated ] = useProductApprovalApi(ProductApprovalRequestName.LIST_APPROVAL_LISTS);
  // permission related to price code
  const checkPermission = useCheckUserPermission();
  const canCreatePriceList = checkPermission("create-price-code");

  const { installedModules, importSettings } = useMappedState(({ global }: IStore) => ({
    installedModules: global.settings ? global.settings.installedModules : [],
    importSettings: global.settings?.importSettings
  }));

  const onFetchData = useCallback((params: {filters: FilterParams<IProductApprovalList>, pagination: PaginatedParams<IProductApprovalList>}) => {
    getApprovalListPaginated({
      pagination: params.pagination,
      filters: params.filters
    });
  }, [ getApprovalListPaginated ]);

  React.useEffect(() => {
    if (triggerFtpImportResponse.data) {
      toast.success(triggerFtpImportResponse.data.message);
    } else if (triggerFtpImportResponse.error) {
      toast.error(triggerFtpImportResponse.error.message);
    }
  }, [ triggerFtpImportResponse.data, triggerFtpImportResponse.error ]);

  const actionButtons = useMemo(() => {
    const likeToDoButtons = [
      <Button
        key="export-approval-lists"
        onClick={() => setShowExportModal(true)}
      >
        Export Approval List
      </Button>
    ];

    const hasProductApprovalCSVImportSetting = _.find(importSettings, is => is.type === ImportSettingType.ProductApproval && is.origin === ImportOrigin.CSV);

    if (canCreatePriceList && hasProductApprovalCSVImportSetting) {
      likeToDoButtons.unshift(
        <Button
          key="import-product-approval-lists"
          onClick={() => setShowImportModal(true)}
        >
          Import Product Approval Lists
        </Button>
      );
    }

    const hasApprovalListSFTPImportSetting = _.find(importSettings, is => is.type === ImportSettingType.ProductApproval && is.origin === ImportOrigin.SFTP);

    if (hasApprovalListSFTPImportSetting && canCreatePriceList) {
      likeToDoButtons.push((
        <Button
          key="create-product"
          onClick={() => triggerFtpImportRequest(hasApprovalListSFTPImportSetting.id)}
        >
          Sync Approval Lists from FTP
        </Button>
      ));
    }

    return likeToDoButtons;
  }, [
    canCreatePriceList,
    importSettings,
    triggerFtpImportRequest
  ]);

  const columns = React.useMemo(() => {
    return getApprovalListColumns({
      actionButton: info => {
        const items = [];

        // check price-change module as approvals closely related
        if (installedModules.includes("price-change")) {
          items.push({
            title: "View",
            action: () => {
              history.push(`/admin/products/approval-lists/${info.row.original.id}/changes`);
            }
          });
        }

        return (
          <ActionButton
            title="Actions"
            items={items}
          />
        );
      }

    });
  }, [ history, installedModules ]);

  return (
    <Layout {...props}>
      <MainContentWrapper
        fill
        Header={(
          <LikeToDo
            copy="What would you like to do?"
            buttons={actionButtons}
          />
        )}
      >

        <Table
          tableOf="Approval Lists"
          data={approvalListsPaginatedResponse.data?.result ? approvalListsPaginatedResponse.data.result : []}
          columns={columns}
          onFetchData={onFetchData}
          loading={approvalListsPaginatedResponse.pending}
          defaultPagination={defaultPagination}
          rowCount={approvalListsPaginatedResponse.data?.count}
          pageCount={approvalListsPaginatedResponse.data?.pages}
        />

      </MainContentWrapper>

      <ImportModal
        open={showImportModal}
        onDismiss={() => setShowImportModal(false)}
        type="product-approval"
        terminology={props.terminology}
      />

      <ProductApprovalExportModal
        open={showExportModal}
        onDismiss={() => setShowExportModal(false)}
      />
    </Layout>
  );
};

export default AprrovalListsPage;
